<template>
  <div class="web-browser side-panel side-panel-with-curtain" @click.self="close">
    <div
      ref="container"
      :class="[
        'panel-container',
        'is-animated',
        'slide-from-right',
        xNewSurfaceShadows ? 'shadow-elevation-3' : 'shadow-z3',
      ]"
    >
      <header class="panel-header panel-header-with-content">
        <div class="panel-left-item">
          <button class="text-button narrow-button" @click="close">{{ __('Close') }}</button>
        </div>
        <div class="panel-title">{{ __('Search') }}</div>
        <div class="panel-right-item" />
      </header>
      <form :class="['panel-header-content', { 'panel-header-content-last': !xTabs }]" @submit.prevent="onSearch">
        <div class="web-browser-form-container">
          <input
            ref="queryInput"
            type="search"
            :value="query"
            :maxlength="MAX_SEARCH_LENGTH"
            autofocus
            :placeholder="__('Enter search term')"
          />
          <button type="submit" class="icon-button" :title="__('Search')"><base-icon name="arrow_forward" /></button>
        </div>
      </form>
      <base-text-tabs
        v-if="xTabs"
        class="web-browser-tabs panel-header-content-last"
        :tabs="tabsData"
        :active-key="activeSearchFilter"
        :click-handler="selectSearchType"
      />
      <div v-if="status != 'results'" class="web-browser-messages" :data-status="status">
        <div v-if="status === 'initial'" class="caption-type web-browser-padded-message">
          {{ initialSearchMessage }}
        </div>
        <OzLoadingSpinner v-else-if="status == 'searching'" />
        <div v-else-if="status == 'noresults'" class="caption-type web-browser-padded-message">
          <p>{{ __('No results found for your search term.') }}</p>
          <p>{{ __('Hope you find what you are looking for!') }}</p>
        </div>
        <div v-else-if="status == 'error'" class="caption-type web-browser-padded-message">
          {{ __('Oops! Something went wrong. Please try again.') }}
        </div>
      </div>
      <div v-else class="web-browser-results" :data-content-type="searchResultContentType">
        <div
          v-if="nextSearchUrl || prevSearchUrl"
          class="web-browser-pagination-control web-browser-pagination-control-top"
        >
          <div class="web-browser-pagination-control-prev">
            <button v-if="prevSearchUrl" class="caption-text-button narrow-button" @click="searchPrev">
              {{ __('Previous') }}
            </button>
          </div>
          <div v-if="showAttribution">
            <web-browser-attribution :provider="searchProvider" />
          </div>
          <div class="web-browser-pagination-control-next">
            <button v-if="nextSearchUrl" class="caption-text-button narrow-button" @click="searchNext">
              {{ __('Next') }}
            </button>
          </div>
        </div>
        <ul v-if="searchResultContentType == 'webpages'" class="tv tv-list">
          <li
            v-for="result in searchResults.webpages"
            :key="result.url"
            class="web-browser-webpage-result tv-cell tv-cell-actionable"
            @click="select(result.url)"
          >
            <h4>{{ result.title }}</h4>
            <p class="small-type">{{ result.description }}</p>
            <p class="small-caption-type ellipsified-type">{{ result.url }}</p>
          </li>
        </ul>
        <ul v-else-if="activeSearchFilter === 'stock'" class="tv tv-list">
          <li
            v-for="result in searchResults.images"
            :key="result.url"
            class="web-browser-result"
            :style="{ width: imageWidth + 'px' }"
            @click="selectWithBeacon(result.url, result.beacon_link)"
          >
            <div class="web-browser-result-preview">
              <web-browser-thumbnail :result="result" :width="imageWidth" :aspect-ratio="1" />
            </div>
            <div class="web-browser-result-meta web-browser-result-meta-with-attribution">
              <div class="web-browser-result-meta-text">
                <div class="small-type">{{ result.title }}</div>
              </div>
              <a class="caption-type" :href="result.attribution_link" target="blank" @click.stop=""
                ><base-icon name="link" size="small"
              /></a>
            </div>
          </li>
        </ul>
        <ul v-else-if="searchResultContentType == 'images'" class="tv tv-list">
          <li
            v-for="result in searchResults.images"
            :key="result.url"
            class="web-browser-result"
            :style="{ width: imageWidth + 'px' }"
            @click="selectWithBeacon(result.url, result.beacon_link)"
          >
            <div class="web-browser-result-preview">
              <web-browser-thumbnail :result="result" :width="imageWidth" />
            </div>
            <div
              v-if="result.attribution_link"
              class="web-browser-result-meta web-browser-result-meta-with-attribution"
            >
              <div class="web-browser-result-meta-text">
                <div class="small-type ellipsified-type" :title="result.title">{{ result.title }}</div>
                <div class="small-caption-type ellipsified-type">{{ providerName(result) }}</div>
                <div class="small-type muted-type ellipsified-type">{{ result.width }}x{{ result.height }}</div>
              </div>
              <a class="caption-type" :href="result.attribution_link" target="blank" @click.stop=""
                ><base-icon name="link" size="small"
              /></a>
            </div>
            <div v-else class="web-browser-result-meta">
              <div v-if="activeSearchFilter == 'images'" class="small-type">{{ result.width }}x{{ result.height }}</div>
              <div class="small-caption-type ellipsified-type">{{ providerName(result) }}</div>
            </div>
          </li>
        </ul>
        <ul v-else-if="searchResultContentType == 'audio'" class="tv tv-list">
          <li
            v-for="result in searchResults.audio"
            :key="result.url"
            class="web-browser-result"
            :style="{ width: imageWidth + 'px' }"
            @click="select(result.url)"
          >
            <div class="web-browser-result-preview">
              <web-browser-thumbnail :result="result" :width="imageWidth" :aspect-ratio="1" />
              <div v-if="result.duration" class="web-browser-result-duration">{{ result.duration }}</div>
            </div>
            <div class="web-browser-result-meta">
              <div class="small-type">{{ result.title }}</div>
              <div class="small-caption-type ellipsified-type" :title="audioDescription(result)">
                {{ audioDescription(result) }}
              </div>
            </div>
          </li>
        </ul>
        <ul v-else-if="searchResultContentType == 'videos'" class="tv tv-list">
          <li
            v-for="result in searchResults.videos"
            :key="result.url"
            class="web-browser-result"
            :style="{ width: imageWidth + 'px' }"
            @click="select(result.url)"
          >
            <div class="web-browser-result-preview">
              <web-browser-thumbnail :result="result" :width="imageWidth" />
              <div v-if="result.duration" class="web-browser-result-duration">{{ result.duration }}</div>
            </div>
            <div class="web-browser-result-meta">
              <div class="small-type">{{ result.title }}</div>
              <div class="small-caption-type ellipsified-type">{{ providerName(result) }}</div>
            </div>
          </li>
        </ul>
        <div v-if="nextSearchUrl || prevSearchUrl" class="web-browser-pagination-control">
          <div class="web-browser-pagination-control-prev">
            <button v-if="prevSearchUrl" class="caption-text-button narrow-button" @click="searchPrev">
              {{ __('Previous') }}
            </button>
          </div>
          <div v-if="showAttribution">
            <web-browser-attribution :provider="searchProvider" />
          </div>
          <div class="web-browser-pagination-control-next">
            <button v-if="nextSearchUrl" class="caption-text-button narrow-button" @click="searchNext">
              {{ __('Next') }}
            </button>
          </div>
        </div>
        <div v-else-if="showAttribution" class="web-browser-attribution-control">
          <web-browser-attribution :provider="searchProvider" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { fetchJson } from '@padlet/fetch'
import { search, searchByUrl } from 'bits/web_search'
import { isUrl, getHostname } from 'bits/url'
import { extractUrl } from 'bits/html_parser'
import localStorage from 'bits/safe_storage'
import BaseIcon from 'vuecomponents/base_icon'
import WebBrowserThumbnail from 'vuecomponents/web_browser_thumbnail'
import WebBrowserAttribution from 'vuecomponents/web_browser_attribution'
import OzLoadingSpinner from 'library/v4/components/OzLoadingSpinner.vue'
import { trackEvent } from 'bits/analytics'
import { captureException } from 'bits/error_tracker'
import { __ } from 'bits/intl'
import EscapeToCancelMixin from 'vuemixins/escape_to_cancel_mixin'
import BaseTextTabs from 'vuecomponents/base_text_tabs.vue'

// In order to simplify the translation of initialSearchMessage,
// we do not allow arbitrary combinations for allowedSources.
const presets = {
  wallpaper: {
    allowedSources: ['stock'],
    initialSearchMessage: __('Search wallpaper gallery.'),
  },
  images: {
    allowedSources: ['images'],
    initialSearchMessage: __('Search images.'),
  },
  imagesAndGifs: {
    allowedSources: ['images', 'gif'],
    initialSearchMessage: __('Search images and GIFs.'),
  },
  all: {
    allowedSources: ['images', 'videos', 'gif', 'audio', 'web'],
    initialSearchMessage: __('Search images, videos, GIFs, audio, websites, and more.'),
  },
}

// https://docs.microsoft.com/en-us/rest/api/cognitiveservices-bingsearch/bing-images-api-v5-reference
// The maximum URL length is 2,048 characters. To ensure that your URL length does not exceed the limit,
// the maximum length of your query parameters should be less than 1,500 characters.
// If the URL exceeds 2,048 characters, the server returns 404 Not found.
// Need to account for percent-encoded multi-code-point UTF-8 string.
const MAX_SEARCH_LENGTH = 100

export default {
  components: { BaseIcon, BaseTextTabs, OzLoadingSpinner, WebBrowserThumbnail, WebBrowserAttribution },
  mixins: [EscapeToCancelMixin],
  props: {
    q: { type: String, default: '' },
    preset: {
      type: String,
      default: 'all',
      validator: (preset) => !!presets[preset],
    },
    xNewSurfaceShadows: {
      type: Boolean,
    },
  },
  data() {
    return {
      query: '',
      activeSearchFilter: localStorage.getItem('activeContentSearchFilter') || 'images',
      searchResults: [],
      nextSearchUrl: null,
      prevSearchUrl: null,
      searchProvider: null,
      isSearching: false,
      error: false,
      imageWidth: 0,
      MAX_SEARCH_LENGTH,
    }
  },
  computed: {
    allowedSources() {
      return presets[this.preset].allowedSources
    },
    xTabs() {
      return this.allowedSources.length > 1
    },
    tabsData() {
      return [
        { text: __('Images'), key: 'images' },
        { text: __('Videos'), key: 'videos' },
        { text: __('Gifs'), key: 'gif' },
        { text: __('Audio'), key: 'audio' },
        { text: __('Web'), key: 'web' },
      ]
    },
    status() {
      let s = null
      if (this.isSearching) {
        s = 'searching'
      } else if (!this.query) {
        s = 'initial'
      } else if (this.error) {
        s = 'error'
      } else {
        const searchResultsList =
          this.searchResults.webpages ||
          this.searchResults.images ||
          this.searchResults.videos ||
          this.searchResults.audio ||
          []
        s = searchResultsList.length === 0 ? 'noresults' : 'results'
      }
      return s
    },
    searchResultContentType() {
      let srct = null
      if (this.searchResults.webpages) {
        srct = 'webpages'
      } else if (this.searchResults.images) {
        srct = 'images'
      } else if (this.searchResults.videos) {
        srct = 'videos'
      } else if (this.searchResults.audio) {
        srct = 'audio'
      } else {
        srct = 'unknown'
      }
      return srct
    },
    showAttribution() {
      return ['giphy', 'spotify', 'tenor', 'unsplash', 'youtube'].includes(this.searchProvider)
    },
    initialSearchMessage() {
      return presets[this.preset].initialSearchMessage
    },
  },
  watch: {
    preset: {
      handler() {
        if (this.allowedSources.includes(this.activeSearchFilter)) return
        localStorage.setItem('activeContentSearchFilter', this.allowedSources[0])
        this.activeSearchFilter = this.allowedSources[0]
      },
      immediate: true,
    },
    query(newValue) {
      if (!newValue) {
        this.searchResults = []
      } else if (isUrl(newValue)) {
        this.select(newValue)
      } else {
        const extractedUrl = extractUrl(newValue)
        if (extractedUrl) {
          this.select(extractedUrl)
        } else {
          this.search({ query: newValue, filter: this.activeSearchFilter })
        }
      }
      // localStorage.setItem("lastContentSearchQuery", newValue || "")
    },
    activeSearchFilter(newValue) {
      trackEvent('Web Browser', 'Changed Filter', newValue)
      localStorage.setItem('activeContentSearchFilter', newValue)
      if (this.query) {
        this.search({ query: this.query, filter: newValue })
        this.$refs.queryInput.focus()
      }
    },
  },
  mounted() {
    trackEvent('Web Browser', 'Mounted', this.activeSearchFilter)
    this.$refs.queryInput.focus()
    // this allows us to accommodate the scrollbar
    this.$refs.container.style.overflow = 'scroll'
    this.imageWidth = Math.floor(this.$refs.container.clientWidth / 2 - 12)
    this.$refs.container.style.overflow = 'auto'
    if (this.q && !isUrl(this.q) && !extractUrl(this.q)) {
      this.query = this.q.substring(0, MAX_SEARCH_LENGTH)
      this.$nextTick(() => {
        this.$refs.queryInput.select()
      })
    }
  },
  methods: {
    providerName(result) {
      if (result.provider_name) return result.provider_name
      return getHostname(result.url)
    },
    audioDescription(result) {
      if (result.release_date) {
        return new Date(result.release_date).toLocaleDateString() + ' – ' + result.description
      }
      return result.description
    },
    search(options) {
      this.isSearching = true
      this.error = false
      trackEvent('Web Browser', 'Searched', options.query)
      if (options.query) {
        search(options.query, options.filter).then(this.handleSearchResponse, this.handleErrorResponse)
      } else {
        searchByUrl(options.url).then(this.handleSearchResponse, this.handleErrorResponse)
      }
    },
    handleSearchResponse(results) {
      this.isSearching = false
      this.searchResults = results.data
      if (results.links) {
        this.prevSearchUrl = results.links.prev
        this.nextSearchUrl = results.links.next
      }
      if (results.meta) {
        this.searchProvider = results.meta.provider
      }
    },
    handleErrorResponse(e) {
      captureException(e)
      this.isSearching = false
      this.error = true
    },
    selectSearchType(newType) {
      this.activeSearchFilter = newType
    },
    cancel() {
      this.close()
    },
    close() {
      this.$emit('close')
    },
    onSearch() {
      this.query = this.$refs.queryInput.value
    },
    select(url) {
      this.$emit('select', url)
    },
    selectWithBeacon(url, beaconLink) {
      if (beaconLink) {
        fetchJson(beaconLink, { cors: true })
      }
      this.$emit('select', url)
    },
    searchNext() {
      this.search({ url: this.nextSearchUrl })
    },
    searchPrev() {
      this.search({ url: this.prevSearchUrl })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
form {
  background-color: white;
  padding: 0px 8px 16px;

  .web-browser-form-container {
    display: flex;
    border-radius: $default-border-radius;
    background: $grey-2;
  }

  button {
    visibility: hidden;
    @include directional-margin($margin-left: 8px, $margin-right: 0);
  }

  input {
    box-shadow: none;
    margin-bottom: 0;
    line-height: 20px;
    padding: 10px 8px;
    box-sizing: border-box;

    &:focus,
    &:active {
      box-shadow: none;
    }
  }

  input:focus,
  input:active,
  input:not(:empty) {
    & + button {
      visibility: visible;
    }
  }
}

.web-browser-tabs {
  height: 36px;
  background: white;
}

.web-browser-results {
  overflow: auto;
  flex-grow: 1;

  h4 {
    font-size: 16px;
  }
}

.web-browser-webpage-result {
  background-color: white;
  margin: 8px;
  width: initial;
  border-radius: $default-border-radius;

  h4 {
    font-weight: 500;
    margin-bottom: 2px;
  }

  p {
    line-height: 17px;
  }
}

[data-content-type='videos'],
[data-content-type='images'],
[data-content-type='audio'] {
  ul {
    display: flex;
    flex-wrap: wrap;
    padding: 4px;
  }

  .web-browser-result {
    position: relative;
    margin: 4px;
    flex-grow: 0;
    flex-shrink: 0;
    box-sizing: border-box;
    cursor: pointer;
    background-color: white;
    border-radius: $default-border-radius;

    img {
      border-top-left-radius: $default-border-radius;
      border-top-right-radius: $default-border-radius;
      display: block;
    }
  }

  .web-browser-result-meta {
    padding: 8px;
  }

  .web-browser-result-meta-text {
    min-height: 18px;
    width: calc(100% - 32px);
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .web-browser-result-meta-with-attribution {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    a {
      text-decoration: none;
      display: block;
      height: 18px;
    }
  }

  .web-browser-result-preview {
    position: relative;
  }

  .web-browser-result-duration {
    position: absolute;
    bottom: 0;
    left: 0;
    background: $caption-text-color;
    color: white;
    padding: 4px 8px;
    font-size: $small-font-size;
  }
}

.web-browser-pagination-control {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 8px 16px;
}

.web-browser-attribution-control {
  padding: 8px 8px 16px;
  text-align: center;
}

.web-browser-pagination-control-top {
  padding: 8px 8px 0;
}

.web-browser-pagination-control-prev,
.web-browser-pagination-control-next {
  width: 100px;
}

.web-browser-pagination-control-next {
  text-align: right;
}

.web-browser-messages {
  flex-grow: 1;
  padding: 32px;
  font-size: $big-body-font-size;
  line-height: $big-body-line-height;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: contain;
  background-position: bottom right;
  background-repeat: no-repeat;

  &[data-status='error'],
  &[data-status='noresults'] {
    background-image: url(../images/cactus_mobile.png);
  }

  &[data-status='initial'] {
    background-image: url(../images/web_browser_backdrop.png);
  }
}
.web-browser-padded-message {
  background-color: $default-background-color;
  padding: 8px;
  border-radius: $default-border-radius;
}
.web-browser-result,
.web-browser-webpage-result {
  @include hover {
    background-color: $highlight-color;
  }
}
</style>
